<template>
  <!-- 全局题库综合指南弹窗：实例挂在 App.vue，任意页面通过 emitter 事件唤起 -->
  <QuestionGuideDialog v-model="visible" :tabs="guideTabs" :active-term-id="activeTermId" />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { QuestionGuideDialog } from './index'
import type { GuideTabGroup } from './QuestionGuideDialog.vue'
import { getGuideConfigApi, type GuideConfigData } from '@/views/high/api/index'
import emitter from '@/utils/mitt'

const visible = ref(false)
const activeTermId = ref<number | string | undefined>(undefined)
const guideConfigData = ref<GuideConfigData | null>(null)

/** 科目 parentCode 中文映射（与 ThisWeek 的指南左表保持一致） */
const PARENT_LABEL: Record<string, string> = {
  speaking: '口语',
  writing: '写作',
  reading: '阅读',
  listening: '听力'
}

/** 指南左表：从 getGuideConfigApi 转换为 GuideTabGroup[] */
const guideTabs = computed<GuideTabGroup[]>(() => {
  const data = guideConfigData.value
  if (!data) return []

  // 过滤掉"题库指南"条目（它是顶部标题，不是列表项），并为特殊条目补齐 parentCode
  const enrichedExtraEntries = data.extraEntries
    .filter((item) => item.termId !== 9001 && item.title !== '题库指南')
    .map((item) => {
      if (item.shortType === 'MCS_MCM' || item.title === 'MCS&MCM') {
        return { ...item, parentCode: 'reading' }
      }
      if (item.shortType === 'OTHER_LISTEN' || item.title?.includes('其他听力')) {
        return { ...item, parentCode: 'listening' }
      }
      return item
    })

  const allItems = [...data.types, ...enrichedExtraEntries]
  // 按 parentCode 分组（只有 4 个科目分组）
  const groups: Record<string, GuideConfigData['types'][number][]> = {}
  allItems.forEach((item) => {
    const key = item.parentCode
    if (!key) return
    if (!groups[key]) groups[key] = []
    groups[key].push(item)
  })
  // 组内按 sort 排序，只保留 4 个科目分组
  const order = ['speaking', 'writing', 'reading', 'listening']
  return order
    .filter((code) => groups[code])
    .map((code) => ({
      category: PARENT_LABEL[code],
      list: groups[code]
        .sort((a, b) => a.sort - b.sort)
        .map((item) => ({
          termId: item.termId,
          label: item.title || item.shortType,
          typeCode: item.shortType,
          questionCountText: item.questionCountText,
          hitRateText: item.hitRateText,
          priority: item.priority
        }))
    }))
})

/** 首次打开时才拉取指南左表配置 */
const loadGuideConfig = () => {
  getGuideConfigApi().then((res) => {
    guideConfigData.value = res.data
  })
}

// 等待左表配置就绪的打开状态（防止 3s 兜底期间重复点击叠加多个定时器）
let waitingOpen = false
let fallbackTimer: number | undefined

/** 打开指南弹窗：termId 指定选中题型（缺省由弹窗内部选中题库综合指南）。
 * 左表配置未就绪时先拉取，就绪后立即打开；3s 仍未就绪兜底直接打开（避免接口失败永久不弹） */
const openGuide = (termId?: number | string) => {
  activeTermId.value = termId
  if (guideTabs.value.length) {
    visible.value = true
    return
  }
  if (waitingOpen) return
  if (!guideConfigData.value) loadGuideConfig()
  waitingOpen = true
  fallbackTimer = window.setTimeout(() => {
    waitingOpen = false
    visible.value = true
  }, 3000)
}

// 左表配置就绪后立即打开（不必等 3s 兜底）
watch(
  () => guideTabs.value.length,
  (len) => {
    if (waitingOpen && len) {
      waitingOpen = false
      window.clearTimeout(fallbackTimer)
      visible.value = true
    }
  }
)

onMounted(() => {
  emitter.on('open-question-guide', ({ termId }) => openGuide(termId))
})

onBeforeUnmount(() => {
  emitter.off('open-question-guide')
  window.clearTimeout(fallbackTimer)
})
</script>
